import { useState, useCallback, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import t from 'common/i18n';
import rootActions, { types as rootAts } from 'root/actions';
import { selectIsInlineMode, selectIsResponsiveWidth } from 'root/selectors';
import CloseButton from 'root/components/CloseButton/CloseButton';
import TabFooter from 'root/components/TabFooter';
import MenuButton from 'root/components/MenuButton/MenuButton';
import Button from 'root/components/Button';
import useExpandedView from 'root/hooks/useExpandedView';
import getMediaQuery from 'utils/sassVarsUtils';
import { NOTIFICATION_IDS } from 'root/data/NotificationsData';
import { CLOSE_PROMPT_WIDTH } from 'common/consts';
import Form from './components/Form';
import Intro from './components/Intro/Intro';
import addValidationMessages from './helpers/addValidationMessages';
import selectors from './selectors';
import SVG_ICONS from '~/assets/icons/icons';

const {
  [rootAts.DO_CLOSE_WIDGET]: closeWidget,
  [rootAts.SET_PROMPT]: setPrompt,
  [rootAts.CLEAR_NOTIFICATION]: clearNotification,
  [rootAts.DO_NAV_TO_PREVIOUS_PATH]: navigateBack,
} = rootActions.creators;

const PageWrapper = styled.div`
	display: flex;
	position: absolute;
	height: 100%;
	width: 100%;
	left: 0;
	right: 0;
	top: 0;
	bottom: 0;
	flex-direction: column;
	//hide the main close button
	z-index: 11;

	// advanced options position
	& .advance-box {
		bottom: 70px;
		left: 0;
		 ${getMediaQuery('ns')} {
		 	left: 72px;
		 	bottom: 16px;
		 }
	},
`;

const Header = styled.div`
  flex-basis: 62px;
  flex-shrink: 0;
  display: flex;
  width: 100%;
`;

const HeaderTitle = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderSubTitle = styled.div`
  font-size: 1rem;
`;

const PageBody = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 30px 15px 0;
  margin-bottom: 70px;
  align-items: center;
  min-height: 0;
  overflow-y: auto;

  @media (max-width: 767px) {
    margin-bottom: 103px; // footer + advanced-options
  }
`;

const PageContent = styled.div`
  max-width: 500px;
`;

const PageIntro = styled.div`
  h2 {
    margin-bottom: 0.5rem;
  }

  p {
    max-width: 450px;
    text-align: left;
    margin: unset;
  }
`;

export default ({ initialState = { formError: false } }) => {
  const formRef = useRef(),
    [formError, setFormRenderError] = useState(initialState.formError),
    dispatch = useDispatch(),
    history = useHistory(),
    isInline = useSelector(selectIsInlineMode),
    uploadFiles = useSelector(selectors.selectUploadFiles),
    schema = useSelector(selectors.selectSchema),
    hasConflicts = useSelector(selectors.selectHasConflictedFiles),
    uploaderCancelUpload = useSelector(selectors.selectUploadCancel),
    isResponsiveWidth = useSelector(selectIsResponsiveWidth);

  useExpandedView();

  const layout = isResponsiveWidth ? 'vertical' : 'horizontal';

  const schemaFields = useMemo(() => addValidationMessages(schema.fields.asMutable({ deep: true })), [schema.fields]);

  const cancelUpload = useCallback(
    (text, backFn, shouldCloseWidget = false) => {
      dispatch(
        setPrompt({
          text,
          width: CLOSE_PROMPT_WIDTH,
          actions: [
            {
              label: t('metadataPage.approveCancel'),
              color: 'error',
              onClick: () => {
                dispatch(clearNotification({ id: NOTIFICATION_IDS.PROMPT }));
                uploaderCancelUpload();
                backFn();

                if (shouldCloseWidget) {
                  dispatch(closeWidget());
                }
              },
            },
          ],
        })
      );
    },
    [uploaderCancelUpload, dispatch]
  );

  const onClose = useCallback(() => {
    cancelUpload(t('metadataPage.closePrompt'), () => dispatch(navigateBack({ history })), true);
  }, [cancelUpload, dispatch, history]);

  const onBackClick = useCallback(() => {
    cancelUpload(t('metadataPage.backPrompt'), () => history.goBack());
  }, [history, cancelUpload]);

  const onUploadClick = useCallback(() => {
    formRef.current.submit();
  }, [formRef]);

  const UploadButton = (
    <Button
      dataTest="metadata-upload-button"
      onClick={onUploadClick}
      className="ml3"
      text={t('metadataPage.upload')}
      icon={SVG_ICONS.UPLOAD}
      colorName="action"
      fontSize={14}
      iconSize={22}
      disabled={formError}
    />
  );

  return (
    <PageWrapper className="bg-source-bg bg-source-bg-c h-auto-ns">
      <Header className="bg-window ph6 bc-window-border">
        <MenuButton onClick={onBackClick} className="relative self-center mr5" dataTest="metadata-back-button" icon={SVG_ICONS.BACK} iconSize={30} />

        <HeaderTitle className="self-center relative pl5 f5 fw6 c-window-border bl">
          <span>{t('metadataPage.title')}</span>
          <HeaderSubTitle className="bg-source-bg-c">{t('metadataPage.subtitle', { count: uploadFiles.length })}</HeaderSubTitle>
        </HeaderTitle>

        {!isInline && <CloseButton onClick={onClose} className="self-center ml5 btn-close-popup ml-auto" data-test="close-dialog-button" />}
      </Header>

      <PageBody className="bg-source-bg bg-source-bg-c">
        <PageContent>
          <PageIntro>
            <Intro hasConflicts={hasConflicts} filesCount={uploadFiles.length} />
          </PageIntro>

          <Form fields={schemaFields} ref={formRef} hasConflicts={hasConflicts} layout={layout} setFormRenderError={setFormRenderError} />
        </PageContent>
      </PageBody>

      <TabFooter hideAdvanced actionButton={UploadButton} />
    </PageWrapper>
  );
};
